import type { GuestSignupTrigger } from '../context/GuestSignupModalContext';

// Locked-tab labels → their authenticated route. For locked_tab_click the
// entityId is the tab label (see the TABS array in PreLoginShell and the gated
// card tag in PreLoginLanding), kept as-is for analytics (signup_trigger_entity_id).
// We map it to the feature route here so a guest who clicked e.g. "Navigator"
// lands on the Navigator after signup, not the generic dashboard. "Dashboard" is
// intentionally absent — the role dashboard is already the default landing.
const LOCKED_TAB_ROUTES: Record<string, string | undefined> = {
  Navigator: '/care-navigator',
  Messages: '/messages',
  'My Network': '/network',
};

/** Maps a gate trigger + entity id to a route path for post-signup deep linking. */
export const deriveDeepLinkPath = (
  trigger: GuestSignupTrigger,
  entityId?: string,
  providerType?: 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR',
): string | undefined => {
  if (!entityId) return undefined;
  switch (trigger) {
    case 'provider_profile_click':
      // FA cards land on /advisors/:id, SP cards on /providers/:id. Without
      // providerType, default to /providers/:id (back-compat with callers that
      // didn't pass it).
      return providerType === 'FINANCIAL_ADVISOR'
        ? `/advisors/${entityId}`
        : `/providers/${entityId}`;
    case 'action_button_click':
      // Feature 076-03: gated actions on a public profile page deep-link back
      // to that profile so the user can finish what they started. Guarded on
      // providerType — other action_button_click callers (resource cards,
      // directory contact buttons) pass no providerType and keep no deep link.
      return providerType
        ? deriveDeepLinkPath('provider_profile_click', entityId, providerType)
        : undefined;
    case 'resource_cap_hit':
      return `/resource-hub-detail/${entityId}`;
    case 'locked_tab_click':
      // entityId is the tab label; map known locked features to their route.
      // Unknown labels (e.g. "Dashboard") fall through to the role dashboard.
      return LOCKED_TAB_ROUTES[entityId];
    default:
      // banner_cta — no deterministic deep link.
      return undefined;
  }
};
